// src/components/forms/DeveloperLoginForm.tsx
import { Form, Input, Button, Tooltip } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { useAuth } from '@/hooks/useAuth';
import { useNavigate, Link } from 'react-router-dom';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { SliderCaptcha } from '@/components/ui/SliderCaptcha';
import type { DeveloperWebLoginRequest } from '@/types/auth';
import type { CaptchaToken } from '@/types/captcha';
import { useState } from 'react';

export const DeveloperLoginForm = () => {
  const { loginDeveloper } = useAuth();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [captchaVisible, setCaptchaVisible] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<CaptchaToken | null>(null);
  const [needCaptcha, setNeedCaptcha] = useState(false);

  // ✅ 抽离核心登录逻辑，避免重复代码
  const doLogin = async (values: DeveloperWebLoginRequest) => {
    setSubmitting(true);
    setError(null);
    try {
      await loginDeveloper(values, navigate);
    } catch (err) {
      let errorMessage = '登录失败';
      if (err instanceof Error) {
        errorMessage = err.message || '登录失败';
        if (errorMessage.includes('401') || errorMessage.includes('unauthorized')) {
          errorMessage = '认证失败：账号或密码错误';
        } else if (errorMessage.includes('network') || errorMessage.includes('timeout')) {
          errorMessage = '网络连接失败，请检查网络。';
        } else if (errorMessage.includes('404')) {
          errorMessage = 'API 接口不存在，请联系管理员。';
        }
      } else if (typeof err === 'string') {
        errorMessage = err;
      } else {
        errorMessage = '登录失败，请检查网络连接或联系管理员。';
      }

      if (!errorMessage.trim()) {
        errorMessage = '登录失败，请稍后重试。';
      }

      setError(errorMessage);
      setCaptchaToken(null); // 登录失败重置验证码
      setNeedCaptcha(true);
    } finally {
      setSubmitting(false);
    }
  };

  const onFinishFailed = (errorInfo: { errorFields: { errors: string[] }[] }) => {
    const firstError = errorInfo.errorFields?.[0]?.errors?.[0] || '表单验证失败';
    setError(firstError);
  };

  const onFinish = async (values: DeveloperWebLoginRequest): Promise<void> => {
    // 1. 需要验证码但未验证：显示验证码弹窗
    if (needCaptcha && !captchaToken?.verified) {
      setCaptchaVisible(true);
      return;
    }

    // 2. 已验证通过：执行登录
    if (captchaToken?.verified) {
      await doLogin(values);
      return;
    }

    // 3. 首次点击：显示验证码
    setCaptchaVisible(true);
    setNeedCaptcha(true);
  };
// ✅ 修复：验证码成功后，先关闭弹窗，再通过 validateFields 验证并登录
const handleCaptchaSuccess = (token: CaptchaToken) => {
  setCaptchaToken(token);
  setCaptchaVisible(false); // 先关闭验证码弹窗

  // 延迟执行：确保 DOM 状态更新完成
  setTimeout(() => {
    // 使用 validateFields 替代 form.submit()，返回 Promise 支持错误处理
    form.validateFields()
      .then((values) => {
        // 表单验证通过，直接调用登录逻辑
        doLogin(values as DeveloperWebLoginRequest);
      })
      .catch((err: unknown) => {
        // 显式声明 err 类型为 unknown，解决 TypeScript 报错
        console.error('表单验证失败:', err);
        // 可选：若验证失败，可重置验证码状态，让用户重新填写后再验证
        // setCaptchaToken(null);
        // setNeedCaptcha(true);
      });
  }, 300); // 300ms 延迟确保状态同步
};

  const handleCaptchaClose = () => {
    setCaptchaVisible(false);
  };

  // 自定义验证规则
  const validateAccount = async (_: unknown, value: unknown) => {
    const inputValue = value as string;
    if (!inputValue) {
      return Promise.reject('请输入手机号或邮箱');
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRegex = /^1[3-9]\d{9}$/;

    if (emailRegex.test(inputValue) || phoneRegex.test(inputValue)) {
      return Promise.resolve();
    }

    return Promise.reject('请输入有效的手机号或邮箱地址');
  };

  return (
    <>
      {error && (
        <ErrorAlert message="登录失败" description={error} />
      )}
      <Form
        form={form}
        name="login"
        layout="vertical"
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
        requiredMark={false}
        className="space-y-8"
      >
        {/* 原有表单内容不变 */}
        <div className="space-y-6">
          <Form.Item
            name="account"
            label={
              <span className="font-medium text-gray-700 flex items-center">
                手机号或邮箱
                <span className="text-red-500 ml-1">*</span>
                <Tooltip title="请输入您注册时使用的手机号或邮箱地址">
                  <QuestionCircleOutlined className="ml-2 text-gray-400 text-sm" />
                </Tooltip>
              </span>
            }
            rules={[{ validator: validateAccount }]}
            className="mb-0"
          >
            <Input
              placeholder="请输入手机号或邮箱"
              size="large"
              disabled={submitting}
              className="rounded-lg border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-colors w-full"
            />
          </Form.Item>

          <div className="space-y-1">
            <div className="w-full">
              <div className="relative">
                <span className="font-medium text-gray-700 flex items-center">
                  登录密码
                  <span className="text-red-500 ml-1">*</span>
                  <Tooltip title="请输入您的登录密码">
                    <QuestionCircleOutlined className="ml-2 text-gray-400 text-sm" />
                  </Tooltip>
                </span>

                <Link
                  to="/forgot-password"
                  className="absolute right-0 top-1/2 -translate-y-1/2 text-sm font-medium text-blue-600 hover:text-blue-500 transition-colors whitespace-nowrap"
                >
                  忘记密码？
                </Link>
              </div>
            </div>

            <Form.Item name="password" noStyle rules={[{ required: true, message: '请输入密码' }]}>
              <Input.Password
                placeholder="请输入登录密码"
                size="large"
                disabled={submitting}
                className="rounded-lg border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-colors w-full"
              />
            </Form.Item>
          </div>
        </div>

        <Form.Item className="mb-0 pt-2">
          <Button
            type="primary"
            htmlType="submit"
            size="large"
            loading={submitting}
            disabled={submitting}
            className="w-full px-9 py-2.5 bg-linear-to-r from-blue-500 to-purple-600 border-0 hover:from-blue-600 hover:to-purple-700 text-white font-medium shadow-md hover:shadow-lg transition-all duration-200 rounded-lg"
          >
            {submitting ? '登录中...' : '登录'}
          </Button>
        </Form.Item>
      </Form>

      <SliderCaptcha
        visible={captchaVisible}
        scene="login"
        onSuccess={handleCaptchaSuccess}
        onClose={handleCaptchaClose}
      />
    </>
  );
};